import { FC, useEffect, useState } from "react";
import { Facture } from "../../lib/interfaces/facture.interface";
import { TableCell, TableRow } from "../ui/table";
import { formatDate } from "../../lib/helpers/date_helpers";
import { Button } from "../ui/button";
import { CheckCircle2, Clock3, Download } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ksFacturesQK } from "../../lib/query-keys";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "../../lib/firebaseClient";
import { useCreatePdfFacture, useFactureDownloadUrl } from '@/lib/hooks/useFacture';

interface Props {
  facture: Facture;
}

const FactureRowItem: FC<Props> = ({ facture: fac }) => {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(fac.is_laravel ? false : true);
  const getDownloadUrlMutation = useFactureDownloadUrl();
  const createPdfMutation = useCreatePdfFacture();

  useEffect(() => {
    if (fac.is_laravel) {
      return;
    }
    const getUrl = async () => {
      setLoading(true);
      const factureRef = ref(storage, fac.filePath);
      const urlFacture = fac.filePath ? await getDownloadURL(factureRef) : "";
      setUrl(urlFacture);
      setLoading(false);
    };
    getUrl();
  }, [storage, fac]);

  const handleDownload = () => {
    if (fac.is_laravel) {
      getDownloadUrlMutation.mutate(fac.id);
    } else if (url) {
      window.open(url, "_blank");
    } else {
      createPdfMutation.mutate(
        { factureId: fac.id },
        {
          onSuccess: () => {
            toast.success("La facture a été générée et va être téléchargée");
            queryClient.invalidateQueries({ queryKey: [ksFacturesQK] });
            getDownloadUrlMutation.mutate(fac.id);
          },
          onError: (err: any) => {
            console.error(err.response?.data || err);
            toast.error(`Une erreur s'est produite`);
          },
        },
      );
    }
  };

  return (
    <TableRow className="block px-4 py-3 hover:bg-slate-50/70 sm:table-row sm:px-0 sm:py-0">
      <TableCell className="flex items-center justify-between gap-4 px-0 py-1.5 text-sm text-slate-600 sm:table-cell sm:px-6 sm:py-4">
        <span className="font-medium text-slate-500 sm:hidden">Date</span>
        <span className="font-medium text-slate-700">{formatDate(fac.date)}</span>
      </TableCell>
      <TableCell className="block px-0 pb-2 pt-1 font-semibold text-slate-900 sm:table-cell sm:p-4">
        {fac.description}
      </TableCell>
      <TableCell className="flex items-center justify-between gap-4 px-0 py-1.5 text-sm sm:table-cell sm:p-4">
        <span className="font-medium text-slate-500 sm:hidden">Montant</span>
        <span className="font-bold tabular-nums text-slate-950">{(fac.montant / 100).toFixed(2)} €</span>
      </TableCell>
      <TableCell className="flex items-center justify-between gap-4 px-0 py-1.5 text-sm sm:table-cell sm:p-4">
        <span className="font-medium text-slate-500 sm:hidden">Statut</span>
        {fac.payee ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#e4efe9] px-2.5 py-1 text-xs font-semibold text-[#173f35]">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Payée
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#fff6d8] px-2.5 py-1 text-xs font-semibold text-[#725716]">
            <Clock3 className="h-3.5 w-3.5" />
            En attente
          </span>
        )}
      </TableCell>
      <TableCell className="block px-0 pb-0 pt-3 text-right sm:table-cell sm:px-6 sm:py-4">
        <Button
          className="w-full bg-[#173f35] hover:bg-[#23594a] sm:w-auto"
          disabled={loading || createPdfMutation.isPending || getDownloadUrlMutation.isPending}
          onClick={handleDownload}
          loading={createPdfMutation.isPending || getDownloadUrlMutation.isPending}
          icon={<Download className="h-4 w-4" />}
          size="sm"
        >
          Télécharger
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default FactureRowItem;
